import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue } from 'jotai';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React from 'react';
import { Badge } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import AssigneeModal from './AssigneeModal';
import {
  findingsDataQueryParamsAtom,
  selectedRowIdsAtom,
  sourceTypeAtom,
  sourceTypeConfig,
} from './state';
import WatcherBulkModal from './WatcherBulkModel';

const BulkActions: React.FC = () => {
  const companyId = useAtomValue(companyIdAtom);
  const sourceType = useAtomValue(sourceTypeAtom);
  const findingsDataQueryParams = useAtomValue(findingsDataQueryParamsAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const count = selectedRowIds.length;
  const [isAssigneeModalOpen, setAssigneeModalOpen] = React.useState(false);
  const [isWatcherBulkModalOpen, setWatcherBulkModalOpen] =
    React.useState(false);

  const appendParamsToUrl = (url, params) => {
    const urlObj = new URL(url, window.location.href);
    const existingParams = new URLSearchParams(urlObj.search);
    const newParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (!existingParams.has(key)) {
        newParams.set(key, value as string);
      }
    }

    const queryString = newParams.toString();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${queryString ? queryString : ''}`;
  };

  // Keep selected IDs out of the URL to avoid very long query strings on CSV export.
  const getExportRequestData = () =>
    selectedRowIds.length > 0 ? { finding_ids: selectedRowIds } : null;

  return (
    <div className="col-auto p-1 text-right align-baseline">
      {count > 0 && (
        <>
          <span className="mr-1" style={{ fontSize: '1.2em' }}>
            <Badge color="primary" pill className="mx-1">
              {count}
            </Badge>
            Selected
          </span>
          <IconButton
            id="clearSelectionButton"
            iconClass="fas fa-times"
            tooltip="Clear all selections"
            onClick={() => setSelectedRowIds([])}
          />
          <span
            className="mx-2"
            style={{
              fontSize: '24px',
              color: 'gray',
              verticalAlign: 'middle',
            }}
          >
            &#124;
          </span>
        </>
      )}
      <IconButton
        id="assignToUserButton"
        iconClass="fa fa-user-plus"
        tooltip="Bulk assign/unassign selected vulnerabilities."
        onClick={function (): void {
          setAssigneeModalOpen(true);
        }}
        disabled={count === 0}
      />
      <IconButton
        id="assignWatchersButton"
        iconClass="fa fa-users"
        tooltip="Bulk add/remove watchers to selected vulnerabilities."
        onClick={function (): void {
          setWatcherBulkModalOpen(true);
        }}
        disabled={count === 0}
      />
      <FileDownloaderButton
        buttonId="vuln_export_all"
        iconClass="fa fa-file-export"
        tooltip={
          sourceType === ''
            ? 'Export vulnerabilities only available for specific asset types. Select an asset type to enable this feature.'
            : `Export vulnerabilities for ${sourceTypeConfig[sourceType]?.label}. All the active filters and row selection will be applied.`
        }
        fileName={`${localStorage
          .getItem('currentCompany')
          ?.toLocaleLowerCase()}_${sourceType}_vulnerabilities.csv`}
        method={selectedRowIds.length > 0 ? 'POST' : 'GET'}
        disabled={sourceType === ''}
        downloadUrl={
          companyId
            ? appendParamsToUrl(
                'findings/new/export-csv',
                findingsDataQueryParams,
              )
            : ''
        }
        className="float-right"
        urlParams={{}}
        data={getExportRequestData()}
        onSuccessCallback={() =>
          showSuccessToast('Success! File download completed.')
        }
        onFailureCallback={(message) => showFailureToast('Error! ' + message)}
      />
      {isAssigneeModalOpen && (
        <AssigneeModal
          isOpen={isAssigneeModalOpen}
          toggle={() => setAssigneeModalOpen(!isAssigneeModalOpen)}
          isBulk
        />
      )}
      {isWatcherBulkModalOpen && (
        <WatcherBulkModal onClose={() => setWatcherBulkModalOpen(false)} />
      )}
    </div>
  );
};

export default BulkActions;
